import type { ReactNode } from 'react';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { COLOR_CONVERTED, LABEL_COLOR, softCardSx } from '../constants';
import type { TrendInfo } from '../types';

export const MetricCard = ({
    label,
    value,
    accent,
    icon,
    trend,
    secondary,
}: {
    label: string;
    value: number;
    accent: string;
    icon: ReactNode;
    trend?: TrendInfo;
    secondary?: { label: string; value: number };
}) => {
    const trendColor =
        trend?.direction === 'up'
            ? COLOR_CONVERTED
            : trend?.direction === 'down'
              ? '#EF4444'
              : LABEL_COLOR;

    return (
        <Card
            elevation={0}
            sx={{
                ...softCardSx,
                flex: 1,
                minWidth: 220,
                borderLeft: `4px solid ${accent}`,
            }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, position: 'relative' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: accent,
                        opacity: 0.85,
                    }}
                >
                    {icon}
                </Box>
                <Typography
                    variant="body2"
                    sx={{ color: LABEL_COLOR, fontWeight: 500, mb: 1, pr: 4 }}
                >
                    {label}
                </Typography>
                <Typography
                    sx={{
                        fontSize: { xs: 32, sm: 36 },
                        fontWeight: 700,
                        lineHeight: 1.1,
                        color: '#111827',
                        mb: trend ? 1 : 0,
                    }}
                >
                    {value}
                </Typography>
                {trend && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        {trend.direction === 'up' && (
                            <TrendingUpIcon sx={{ fontSize: 16, color: trendColor }} />
                        )}
                        {trend.direction === 'down' && (
                            <TrendingDownIcon sx={{ fontSize: 16, color: trendColor }} />
                        )}
                        {trend.direction === 'flat' && (
                            <TrendingFlatIcon sx={{ fontSize: 16, color: trendColor }} />
                        )}
                        <Typography variant="caption" sx={{ color: trendColor, fontWeight: 500 }}>
                            {trend.text}
                        </Typography>
                    </Stack>
                )}
                {secondary && (
                    <Box
                        sx={{
                            mt: 1.5,
                            pt: 1.25,
                            borderTop: '1px solid #E5E7EB',
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ color: LABEL_COLOR, fontWeight: 500, display: 'block' }}
                        >
                            {secondary.label}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 22,
                                fontWeight: 700,
                                lineHeight: 1.1,
                                color: '#111827',
                            }}
                        >
                            {secondary.value}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};
